import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SHOP_LIMIT_REFUSAL } from '@servwell/shared';
import { authClient } from '../../lib/auth-client';
import { useTRPC } from '../../lib/trpc';
import { beginIdentityTransition } from '../../lib/identity';
import { OFFLINE_REASON, useSignOutGuard } from './SignOutButton';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';
import { BrandMark } from './BrandMark';

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'shop'
  );
}

/**
 * Second screen ever seen: naming the shop. The old copy explained tenancy ("your shop is
 * your tenant, isolated from every other shop") — true, and not a sentence a plumber needs
 * on their first minute. What they need to know is that the name shows up on what their
 * customers receive, and that it can be changed later.
 */
/**
 * Better Auth's refusal of a new shop, in this screen's words where it is one we know: the 4th
 * shop (`organizationLimit`, MAP 249 spec §12) says "shops", not Better Auth's "organizations".
 * Anything else is shown as it came.
 */
export function createShopRefusal(
  error: { code?: string; message?: string } | null | undefined,
): string {
  if (error?.code === 'YOU_HAVE_REACHED_THE_MAXIMUM_NUMBER_OF_ORGANIZATIONS') {
    return SHOP_LIMIT_REFUSAL;
  }
  return error?.message ?? 'Could not set your shop up. Try again.';
}

export function CreateOrgScreen() {
  // No shop, so no queued work of a shop's to warn about; offline it still cannot sign out.
  const signOutGuard = useSignOutGuard(null);
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // The random tail keeps two shops with the same name from colliding on the slug.
      const slug = `${slugify(name)}-${Math.random().toString(36).slice(2, 6)}`;
      // Creating a shop makes it the active one, and so does `setActive` below: other tabs
      // stop drawing the old account before either can land (MAP 225a).
      beginIdentityTransition();
      const res = await authClient.organization.create({ name: name.trim(), slug });
      if (res.error || !res.data) {
        setError(createShopRefusal(res.error));
      } else {
        await authClient.organization.setActive({ organizationId: res.data.id });
        await qc.invalidateQueries({ queryKey: trpc.me.session.queryKey() });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-5 p-6">
      <div className="flex items-center gap-3">
        <BrandMark className="size-11" />
        <h1 className="font-heading text-xl font-bold">Set up your shop</h1>
      </div>

      <SectionCard title="What's the business called?">
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-muted-foreground">
            This is the name your customers see on quotes, invoices and anything you send
            them. You can change it later in Settings.
          </p>
          <Field label="Shop name">
            <input
              className={field}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Precision Heating &amp; Air"
              autoFocus
              required
            />
          </Field>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <Button type="submit" disabled={busy || !name.trim()} className="w-full">
            {busy ? 'Setting it up…' : 'Create shop'}
          </Button>
        </form>
      </SectionCard>

      <button
        type="button"
        className={`text-center text-sm text-muted-foreground hover:underline ${signOutGuard.offline ? 'opacity-60' : ''}`}
        onClick={signOutGuard.request}
        aria-busy={signOutGuard.asking || undefined}
        aria-describedby={signOutGuard.offline ? signOutGuard.reasonId : undefined}
      >
        Sign out
      </button>
      {signOutGuard.offline ? (
        <p
          id={signOutGuard.reasonId}
          className="text-center text-xs text-muted-foreground"
        >
          {OFFLINE_REASON}
        </p>
      ) : null}
      {signOutGuard.dialog}
    </div>
  );
}
